import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { ChangeInfo } from '@api/models/suppliers/ChangeInfo';
import { UnmetConditionDto } from '@api/models/visitations/UnmetConditionDto';
import QcsAlert from '../common/basic/QcsAlert';
import { QcsTypography } from '../common/basic/QcsTypography';
import { formatDate } from '../../utils/date';
import { getTranslatedError } from '../../utils/error';

/** Matches a value that carries a clock time, so a plain code or count is never mistaken for one. */
const TIME_VALUE = /\d{1,2}:\d{2}/;

/**
 * Renders a condition parameter the way a refused entry renders it: a time as HH:mm,
 * with seconds only when they carry information. Anything else is passed through untouched.
 */
const formatParameterValue = (value: unknown, language: string) => {
  if (typeof value !== 'string' || !TIME_VALUE.test(value)) return value;

  const dateTime = DateTime.fromISO(value);
  if (!dateTime.isValid) return value;

  return dateTime.setLocale(language).toLocaleString(dateTime.second ? DateTime.TIME_WITH_SECONDS : DateTime.TIME_SIMPLE);
};

const formatParameters = (parameters: UnmetConditionDto['parameters'], language: string) => {
  if (!parameters) return undefined;
  return Object.fromEntries(Object.entries(parameters).map(([key, value]) => [key, formatParameterValue(value, language)]));
};

interface VisitationEntryReleaseProps {
  entryReleased: ChangeInfo;
  unmetConditions?: UnmetConditionDto[];
}

/**
 * Banner shown on a visitation whose entry was released despite unmet conditions.
 * Turnstile entries carry the release without the conditions — the turnstile never records what was wrong.
 */
export const VisitationEntryRelease: FC<VisitationEntryReleaseProps> = ({ entryReleased, unmetConditions }) => {
  const { t, i18n } = useTranslation();
  const conditions = unmetConditions ?? [];
  const at = formatDate(i18n, entryReleased.at, true, true);
  const name = entryReleased.byApiClient ? t('integration.apiAccess.changedBy', { name: entryReleased.by?.name }) : entryReleased.by?.name;

  return (
    <QcsAlert severity="info" sx={{ width: '100%' }}>
      <QcsTypography variant="body2">
        {t(conditions.length ? 'visitation.entryRelease.titleWithConditions' : 'visitation.entryRelease.title')}
      </QcsTypography>
      {conditions.length > 0 && (
        <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0' }}>
          {conditions.map((condition) => (
            <li key={condition.code}>
              {condition.actionRequiredBy && <strong>{t(`visitation.accessPointLog.${condition.actionRequiredBy.toLowerCase()}`)}: </strong>}
              {getTranslatedError(condition.code, formatParameters(condition.parameters, i18n.language))}
            </li>
          ))}
        </ul>
      )}
      <QcsTypography variant="body2">
        {name ? t('visitation.entryRelease.releasedBy', { name, at }) : t('visitation.entryRelease.releasedAt', { at })}
      </QcsTypography>
    </QcsAlert>
  );
};
